import { HTTP_STATUS_CODES } from '@/helpers/http'
import loginFormValidationMixin from '@/mixins/login-form-validation'
import orderUpdateMixin from '@/mixins/order-update'

export default {
  mixins: [loginFormValidationMixin, orderUpdateMixin],

  methods: {
    resetForm() {
      this.resetFormBase()
      this.fieldsArray = this.getFieldsArray()
      this.updateVisibilityOfFields()
      this.resetShowPassword()
    },

    resetRememberMeFlag(fallbackValue) {
      const shouldRememberMe =
        this.actionType != 'login' || !this.isDesktopViewport || fallbackValue
      this.formData.remember_me = shouldRememberMe ? 1 : 0
    },

    onHelperCheckboxClick() {
      if (this.actionType == 'login' && !this.isDesktopViewport) {
        this.toggleShowPassword()
      }
    },

    resetShowPassword() {
      this.helperCheckbox = false
      this.toggleShowPassword('password')
    },

    toggleShowPassword(forcedType) {
      const updateFieldType = fieldKey => {
        this.updateField(fieldKey, field => {
          const type = forcedType || field.type == 'text' ? 'password' : 'text'
          return { type }
        })
      }
      updateFieldType('password')
      updateFieldType('password_confirmation')
    },

    toggleHasActiveInput(value) {
      const commit = () => this.$store.commit('nav/setAccountNavMustStayOpen', value)

      if (value) {
        clearTimeout(this.hasActiveInputTimeout)
        commit()
      } else {
        // To avoid jumps in animation of navigation (hide then show) when
        // selecting an autocomplete option generated by the browser
        this.hasActiveInputTimeout = setTimeout(commit, 200)
      }
    },

    updateVisibilityOfFields() {
      this.updateField('password_confirmation', () => {
        return { hidden: this.actionType != 'signUp' }
      })
    },

    onFocusOfAnyField() {
      if (this.variant == 'navigation') this.toggleHasActiveInput(true)
    },

    onBlurOfAnyField() {
      if (this.variant == 'navigation') this.toggleHasActiveInput(false)
    },

    getFieldsArray() {
      const defaultEventHandlers = {
        focus: () => this.onFocusOfAnyField(),
        blur: () => this.onBlurOfAnyField(),
      }
      return this.formatFieldsArray([
        {
          key: 'email',
          type: 'text',
          label: this.$t('checkout.login.email'),
          isInvalid: value => this.isEmailInvalid(value),
          formatter: value => value.toLowerCase(),
          on: {
            ...defaultEventHandlers,
            blur: e => this.onEmailBlur(e.target.value),
          },
        },
        {
          key: 'password',
          type: 'password',
          label: this.$t('checkout.login.password'),
          isInvalid: value => this.isPasswordInvalid(value),
          autocomplete: false,
          groups: ['password'],
          on: defaultEventHandlers,
        },
        {
          key: 'password_confirmation',
          type: 'password',
          label: this.$t('checkout.login.password_confirmation'),
          isInvalid: value => this.isPasswordConfirmationInvalid(value),
          autocomplete: false,
          groups: ['password'],
          on: defaultEventHandlers,
        },
      ])
    },

    onEmailBlur(email) {
      this.onBlurOfAnyField()
      if (this.variant == 'checkout') this.updateOrderInSalesforce(email)
    },

    trackConnectionInGoogleAnalytics() {
      ga('send', 'event', 'User Account', this.actionType)

      if (this.variant == 'checkout')
        ga('send', 'event', 'Checkout', 'registration', this.actionType)
    },

    trackConnectionInDynamicYield(email, accountInfo) {
      if (this.actionType == 'signUp') {
        On.DynamicYield.trackAccountSignup(email)
        if (accountInfo.newsletterSubscribed) {
          On.DynamicYield.trackNewsletterSubscription(email)
        }
      } else if (this.actionType == 'login') {
        On.DynamicYield.trackAccountLogin(email)
      }
    },

    validateFormThenSubmit() {
      if (this.preventFormSubmit) return

      let callback
      if (this.variant == 'navigation') {
        callback = () => {
          if (!this.submittedSuccessfully) {
            const errorFieldKey = this.errorCount
              ? this.fieldsArray.find(field => field.errors.length > 0).key
              : 'password'
            this.$refs[errorFieldKey][0].$refs.field.focus()
          }
        }
      }
      this.validateForm(() => this.submitLoginForm(callback))
    },

    submitLoginForm(callback = () => {}) {
      if (this.isSubmitting || this.isRedirecting) return

      const permittedObject = this.getPermittedObject(this.permittedFields, this.formData)
      const formData = { spree_user: permittedObject }

      if (this.newsletterSubscribed) {
        formData.newsletter_subscribed = true
      }
      const emailSubmitted = permittedObject.email
      const axiosPromise = this.$http.post(this.paths[this.actionType], formData)

      this.submitForm(axiosPromise, {
        then: ({ data }) => {
          this.isRedirecting = true
          this.trackConnectionInGoogleAnalytics()
          this.trackConnectionInDynamicYield(emailSubmitted, data.user)

          if (this.variant == 'navigation') {
            window.location.reload(true)
          } else {
            window.location.href =
              this.variant == 'checkout' ? this.paths.stepAfterLogin : data.redirect
          }
        },
        catch: error => {
          if (error?.response?.status === HTTP_STATUS_CODES.tooManyRequests) {
            this.errorMessage = this.$t('errors.login.too_many_requests.login_page')
          }
        },
        finally: callback,
      })
    },
  },
}
